import store from '@/store/store'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { openPremiumPrompt } from '@/services/ai/openPremiumPrompt'
import { localDayKey } from '@/utils/userActivity'
import { cancelWaitingLimitedOffers } from '@/components/ShowModal/LimitedOffer/limitedOfferTrigger'

export const EDITOR_DOWNLOAD_PREMIUM_SOURCE = 'BtSubscriptionDownload'
const HISTORY_PREFIX = 'editorDownloadPrompt:v1:'

export interface DownloadPromptResult {
  resume: boolean
  prompted: boolean
}

function userId(): string | null {
  const user = store.getState().editor.user.user
  const id = user?.userId ?? user?.id
  return id == null ? null : String(id)
}

function screenBusy(): boolean {
  const state = store.getState()
  return state.modal.showModalType !== null || state.editor.user.openModalOutOfCredits.isOpen ||
    Boolean(state.editor.user.introVideoDetail?.isOpen)
}

/** Only the ordinary editor's final Download opts into this optional prompt. */
export function waitForEditorDownloadPrompt(
  signal: AbortSignal,
  isCurrent: () => boolean,
): Promise<DownloadPromptResult> {
  if (signal.aborted || !isCurrent()) return Promise.resolve({ resume: false, prompted: false })
  const id = userId()
  const state = store.getState()
  if (!id || state.editor.user.userIsPremium || state.editor.user.openModalTryPremium.isOpen || screenBusy()) {
    return Promise.resolve({ resume: true, prompted: false })
  }
  const key = `${HISTORY_PREFIX}${encodeURIComponent(id)}`
  const today = localDayKey(Date.now())
  try {
    const lastShown = localStorage.getItem(key)
    if (lastShown !== null) {
      const parsed = /^\d{4}-\d{2}-\d{2}$/.test(lastShown)
      if (!parsed || lastShown >= today) return Promise.resolve({ resume: true, prompted: false })
    }
  } catch {
    // A daily cap that cannot be read must not hold the user's download.
    return Promise.resolve({ resume: true, prompted: false })
  }

  return new Promise(resolve => {
    let settled = false
    let prompted = false
    let unsubscribe = () => {}
    const ownsPrompt = () => {
      const prompt = store.getState().editor.user.openModalTryPremium
      return prompt.source === EDITOR_DOWNLOAD_PREMIUM_SOURCE && prompt.callback === upgraded
    }
    const current = () => !signal.aborted && userId() === id && isCurrent()
    const finish = (resume: boolean) => {
      if (settled) return
      settled = true
      unsubscribe()
      signal.removeEventListener('abort', cancel)
      if (ownsPrompt()) {
        const prompt = store.getState().editor.user.openModalTryPremium
        store.dispatch(setOpenModalTryPremium({ isOpen: false, source: null, opened: prompt.opened }))
      }
      resolve({ resume: resume && current(), prompted })
    }
    const cancel = () => finish(false)
    const visible = () => {
      if (settled || !current() || !ownsPrompt() || screenBusy()) return false
      if (prompted) return true
      try {
        const shownDay = localDayKey(Date.now())
        // Another tab may have spent today's impression after this request began.
        if (localStorage.getItem(key) === shownDay) { finish(true); return false }
        localStorage.setItem(key, shownDay)
      } catch {
        finish(true)
        return false
      }
      prompted = true
      cancelWaitingLimitedOffers()
      return true
    }
    const dismiss = () => { visible(); finish(true) }
    const upgraded = () => {
      if (!store.getState().editor.user.userIsPremium) return
      visible()
      finish(true)
    }
    unsubscribe = store.subscribe(() => {
      if (!current() || screenBusy() || !ownsPrompt()) cancel()
    })
    signal.addEventListener('abort', cancel, { once: true })
    openPremiumPrompt({ source: EDITOR_DOWNLOAD_PREMIUM_SOURCE, type: 'Standard', callback: upgraded,
      continuation: { onVisible: visible, onDismiss: dismiss },
    })
  })
}
